import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Line } from 'react-native-svg';
import { useDispatch, useSelector } from 'react-redux';

import MinusIcon from '../../../assets/vipRooms/minus.svg';
import PlusIcon from '../../../assets/vipRooms/plus.svg';
import VipRoomsPng from '../../../assets/vipRooms/vip-rooms.png';
import { RootState } from '../../../store';
import {
	clearCreateTableError,
	createSpecialTableRequest,
	myTablesRequest,
} from '../../../store/lobby/lobbyActions';
import { formatBigNumbers } from '../../../utils/helpers/numberFormat';
import { RootStackParamList } from '../../navigator';
import { CtaButton } from '../common/CtaButton';
import { Pressable } from '../common/Touchable';
import { FONT } from '../Profile/BuyVip/vipClubShared';
import ErrorVipModal from './ErrorCreateVipTableModal';
import RedesignModal from './RedesignModal';
import { PopupToggle } from './RedesignPopup';
import { useRedesignScale } from './useRedesignScale';

const PANEL = { height: 678, radius: 16, rim: 1 };
const LAYOUT = {
	side: 16,
	headerTop: 16,
	iconTitleGap: 2,
	titleDividerGap: 8,
	icon: 80,
	titleHeight: 32,
	dividerBottom: 5,
	fieldGap: 16,
	labelHeight: 18,
	labelGap: 8,
	inputHeight: 54,
	stepper: 36,
	inputPadding: 8,
	lockedGap: 16,
	actionsTop: 16,
	actionGap: 12,
	actionHeight: 48,
	bottom: 16,
};

const colors = {
	border: '#FCF17B',
	divider: '#FFD973',
	input: '#330E07',
	inputBorder: '#5C2416',
	label: '#FFF5B9',
	value: '#FFD766',
	cancelBorder: '#DA600E',
	cancelLabel: '#D9C4BC',
	scrim: 'rgba(0,0,0,0.68)',
	panelTint: 'rgba(61,4,0,0.78)',
} as const;

type Nav = NativeStackNavigationProp<RootStackParamList>;

interface VipTablesModalProps {
	onClose: () => void;
}

const TURN_TIMES = [10, 15, 20, 30, 60];

const STAKES_CONFIG = [
	{ name: 'beginner', stake: 5 },
	{ name: 'rookie', stake: 20 },
	{ name: 'advanced', stake: 100 },
	{ name: 'semi', stake: 200 },
	{ name: 'profesional', stake: 500 },
	{ name: 'talanted', stake: 1000 },
];

const ALL_STRUCTURES = [
	{ label: 'short', min: 50, max: 250, forStake: 5 },
	{ label: 'normal', min: 100, max: 500, forStake: 5 },
	{ label: 'high', min: 250, max: 750, forStake: 5 },
	{ label: 'deep', min: 500, max: 1000, forStake: 5 },
	{ label: 'unlimited', min: 500, max: 50000000, forStake: 5 },
	{ label: 'short', min: 200, max: 1000, forStake: 20 },
	{ label: 'normal', min: 400, max: 2000, forStake: 20 },
	{ label: 'high', min: 1000, max: 3000, forStake: 20 },
	{ label: 'deep', min: 2000, max: 4000, forStake: 20 },
	{ label: 'unlimited', min: 2000, max: 200000000, forStake: 20 },
	{ label: 'short', min: 1000, max: 5000, forStake: 100 },
	{ label: 'normal', min: 2000, max: 10000, forStake: 100 },
	{ label: 'high', min: 5000, max: 15000, forStake: 100 },
	{ label: 'deep', min: 10000, max: 20000, forStake: 100 },
	{ label: 'unlimited', min: 10000, max: 1000000000, forStake: 100 },
	{ label: 'short', min: 2000, max: 10000, forStake: 200 },
	{ label: 'normal', min: 4000, max: 20000, forStake: 200 },
	{ label: 'high', min: 10000, max: 30000, forStake: 200 },
	{ label: 'short', min: 5000, max: 25000, forStake: 500 },
	{ label: 'normal', min: 10000, max: 50000, forStake: 500 },
	{ label: 'normal', min: 10000, max: 50000, forStake: 1000 },
];

const formatChips = (value: number) => formatBigNumbers(value, { fractionDigits: 1 });

const VipTablesModal = ({ onClose }: VipTablesModalProps) => {
	const dispatch = useDispatch();
	const scale = useRedesignScale({ fitHeight: PANEL.height, minFitScale: 0.8 });
	const s = (n: number) => n * scale;
	const styles = useMemo(() => createStyles(scale), [scale]);
	const { navigate } = useNavigation<Nav>();
	const { t } = useTranslation();

	const [showErrorModal, setShowErrorModal] = useState(false);
	const [errorModalMessage, setErrorModalMessage] = useState('');
	const [stakeIndex, setStakeIndex] = useState(0);
	const [structureIndex, setStructureIndex] = useState(0);
	const [seats, setSeats] = useState(8);
	const [turnTimeIndex, setTurnTimeIndex] = useState(3);
	const [isLocked, setIsLocked] = useState(true);

	const createdTableId = useSelector((state: RootState) => state.lobby.createdTableId);
	const createdTableLegacyRoomId = useSelector(
		(state: RootState) => state.lobby.createdTableLegacyRoomId,
	);
	const isCreating = useSelector((state: RootState) => state.lobby.createTableLoading);
	const createTableError = useSelector((state: RootState) => state.lobby.createTableError);
	const availableStructures = ALL_STRUCTURES.filter(
		structure => structure.forStake === STAKES_CONFIG[stakeIndex].stake,
	);

	useEffect(() => {
		dispatch(myTablesRequest());
	}, [dispatch]);

	useEffect(() => {
		setStructureIndex(0);
	}, [stakeIndex]);

	useEffect(() => {
		if (createTableError) {
			setErrorModalMessage(
				createTableError.startsWith('T_SITE_') ? t(createTableError) : createTableError,
			);
			setShowErrorModal(true);
		}
	}, [createTableError, t]);

	useEffect(() => {
		if (createdTableId) {
			onClose();
			navigate('Game', {
				tableId: String(createdTableId),
				legacyRoomId: createdTableLegacyRoomId,
				roomSlug: 'vip',
				isTournamentRoom: false,
			});
		}
	}, [createdTableId, createdTableLegacyRoomId, navigate, onClose]);

	const step = (
		setIndex: React.Dispatch<React.SetStateAction<number>>,
		length: number,
		delta: number,
	) => setIndex(index => (index + delta + length) % length);

	const handleCreate = () => {
		const stakeConfig = STAKES_CONFIG[stakeIndex];
		const structureConfig = availableStructures[structureIndex];
		dispatch(
			createSpecialTableRequest({
				stake: stakeConfig.stake,
				minimalAmount: structureConfig.min,
				maximalAmount: structureConfig.max,
				maxSeats: Math.min(seats, 8),
				turnDuration: TURN_TIMES[turnTimeIndex],
				isPrivate: isLocked,
			}),
		);
	};

	const stepper = (
		label: string,
		value: string,
		onPrevious: () => void,
		onNext: () => void,
		previousDisabled = false,
		nextDisabled = false,
	) => (
		<View style={styles.field}>
			<Text style={styles.label}>{label.toUpperCase()}</Text>
			<View style={styles.input}>
				<Pressable
					accessibilityRole='button'
					accessibilityLabel={t('T_SITE_A11Y_PREVIOUS_OPTION', {
						defaultValue: 'Previous {{name}}',
						name: label,
					})}
					disabled={previousDisabled}
					onPress={onPrevious}
					style={[styles.stepper, previousDisabled && styles.disabled]}
				>
					<MinusIcon width={s(26)} height={s(19)} />
				</Pressable>
				<Text numberOfLines={1} style={styles.value}>
					{value}
				</Text>
				<Pressable
					accessibilityRole='button'
					accessibilityLabel={t('T_SITE_A11Y_NEXT_OPTION', {
						defaultValue: 'Next {{name}}',
						name: label,
					})}
					disabled={nextDisabled}
					onPress={onNext}
					style={[styles.stepper, nextDisabled && styles.disabled]}
				>
					<PlusIcon width={s(26)} height={s(26)} style={{ transform: [{ translateY: s(4) }] }} />
				</Pressable>
			</View>
		</View>
	);

	return (
		<>
			<RedesignModal
				scale={scale}
				onClose={onClose}
				panel={{
					height: PANEL.height,
					radius: PANEL.radius,
					borderColor: colors.border,
					borderWidth: PANEL.rim,
					tint: colors.panelTint,
					scrimColor: colors.scrim,
					overScreen: true,
				}}
			>
				<View style={styles.content}>
					<View style={styles.header}>
						<Image source={VipRoomsPng} resizeMode='contain' style={styles.vipRooms} />
						<Text style={styles.title}>{t('T_SITE_MODAL_NEW_ROOM_TITLE')}</Text>
						<View style={styles.divider} />
						<Pressable
							accessibilityRole='button'
							accessibilityLabel={t('T_SITE_ACHIEVEMENTS_CLOSE', { defaultValue: 'Close' })}
							onPress={onClose}
							hitSlop={6}
							style={styles.close}
						>
							<Svg width={s(24)} height={s(24)} viewBox='0 0 24 24'>
								<Line x1={5} y1={5} x2={19} y2={19} stroke='#FFF5B9' strokeWidth={2.5} />
								<Line x1={19} y1={5} x2={5} y2={19} stroke='#FFF5B9' strokeWidth={2.5} />
							</Svg>
						</Pressable>
					</View>

					<View style={styles.body}>
						{stepper(
							t('T_SITE_MODAL_NEW_ROOM_STAKE'),
							`${t(`T_SITE_MODAL_NEW_ROOM_${STAKES_CONFIG[stakeIndex].name.toUpperCase()}`)} (${STAKES_CONFIG[stakeIndex].stake})`,
							() => step(setStakeIndex, STAKES_CONFIG.length, -1),
							() => step(setStakeIndex, STAKES_CONFIG.length, 1),
						)}
						{stepper(
							t('T_SITE_MODAL_NEW_ROOM_CHIP_STRUCTURE'),
							`${t(`T_SITE_MODAL_NEW_ROOM_${availableStructures[structureIndex].label.toUpperCase()}`)} (${formatChips(availableStructures[structureIndex].min)} - ${formatChips(availableStructures[structureIndex].max)})`,
							() => step(setStructureIndex, availableStructures.length, -1),
							() => step(setStructureIndex, availableStructures.length, 1),
						)}
						{stepper(
							t('T_SITE_MODAL_NEW_ROOM_SEATS'),
							String(seats),
							() => setSeats(value => Math.max(2, value - 1)),
							() => setSeats(value => Math.min(8, value + 1)),
							seats <= 2,
							seats >= 8,
						)}
						{stepper(
							t('T_SITE_MODAL_NEW_ROOM_TUNR_DURATION'),
							t(`T_SITE_MODAL_NEW_ROOM_${TURN_TIMES[turnTimeIndex]}_SEC`),
							() => step(setTurnTimeIndex, TURN_TIMES.length, -1),
							() => step(setTurnTimeIndex, TURN_TIMES.length, 1),
						)}
						<View style={styles.locked}>
							<Text style={styles.label}>{t('T_SITE_MODAL_NEW_ROOM_LOCKED').toUpperCase()}</Text>
							<PopupToggle
								value={isLocked}
								onChange={() => setIsLocked(value => !value)}
								scale={scale}
							/>
						</View>
						<View style={styles.actions}>
							<Pressable
								accessibilityRole='button'
								disabled={isCreating}
								onPress={onClose}
								style={[styles.cancel, isCreating && styles.disabled]}
							>
								<Text style={styles.cancelLabel}>
									{t('T_SITE_MODAL_NEW_ROOM_CANCEL').toUpperCase()}
								</Text>
							</Pressable>
							<CtaButton
								accessibilityLabel={t('T_SITE_MODAL_NEW_ROOM_SUBMIT')}
								disabled={isCreating}
								label={t('T_SITE_MODAL_NEW_ROOM_SUBMIT').toUpperCase()}
								onPress={handleCreate}
								size='medium'
								style={styles.createButton}
								labelStyle={styles.createLabel}
							/>
						</View>
					</View>
				</View>
			</RedesignModal>

			<ErrorVipModal
				visible={showErrorModal}
				title={t('T_ERROR', { defaultValue: 'Error' })}
				message={errorModalMessage}
				onClose={() => {
					setShowErrorModal(false);
					dispatch(clearCreateTableError());
				}}
				buttonText={t('T_OK', { defaultValue: 'OK' })}
			/>
		</>
	);
};

const createStyles = (scale: number) => {
	const s = (n: number) => n * scale;
	return StyleSheet.create({
		content: { flex: 1 },
		header: {
			alignItems: 'center',
			paddingTop: s(LAYOUT.headerTop),
			paddingHorizontal: s(LAYOUT.side),
		},
		vipRooms: {
			height: s(LAYOUT.icon),
			marginBottom: s(LAYOUT.iconTitleGap),
			width: s(LAYOUT.icon),
		},
		title: {
			color: '#FFD766',
			fontFamily: FONT,
			fontSize: s(28),
			fontWeight: '900',
			height: s(LAYOUT.titleHeight),
			lineHeight: s(LAYOUT.titleHeight),
			marginBottom: s(LAYOUT.titleDividerGap),
			textAlign: 'center',
			textShadowColor: 'rgba(89,15,0,0.9)',
			textShadowOffset: { width: 0, height: s(2) },
			textShadowRadius: s(4),
		},
		divider: {
			backgroundColor: colors.divider,
			height: Math.max(1, s(1)),
			marginBottom: s(LAYOUT.dividerBottom),
			opacity: 0.75,
			width: '100%',
		},
		close: {
			alignItems: 'center',
			height: s(44),
			justifyContent: 'center',
			position: 'absolute',
			right: s(12),
			top: s(12),
			width: s(44),
		},
		body: {
			flex: 1,
			paddingBottom: s(LAYOUT.bottom),
			paddingHorizontal: s(LAYOUT.side),
			rowGap: s(LAYOUT.fieldGap),
		},
		field: { rowGap: s(LAYOUT.labelGap) },
		label: {
			color: colors.label,
			fontFamily: 'Sofia Sans Condensed',
			fontSize: s(16),
			fontWeight: '900',
			height: s(LAYOUT.labelHeight),
			letterSpacing: s(0.64),
			lineHeight: s(LAYOUT.labelHeight),
			textAlign: 'center',
		},
		input: {
			alignItems: 'center',
			backgroundColor: colors.input,
			borderColor: colors.inputBorder,
			borderRadius: s(8),
			borderWidth: Math.max(1, s(1)),
			flexDirection: 'row',
			height: s(LAYOUT.inputHeight),
			paddingHorizontal: s(LAYOUT.inputPadding),
		},
		stepper: {
			alignItems: 'center',
			backgroundColor: '#300900',
			borderColor: '#FFDB12',
			borderRadius: s(LAYOUT.stepper / 2),
			borderWidth: Math.max(1, s(1)),
			height: s(LAYOUT.stepper),
			justifyContent: 'center',
			shadowColor: '#FF9D00',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.7,
			shadowRadius: s(5),
			width: s(LAYOUT.stepper),
		},
		value: {
			color: colors.value,
			flex: 1,
			fontFamily: FONT,
			fontSize: s(20),
			fontWeight: '700',
			lineHeight: s(24),
			paddingHorizontal: s(4),
			textAlign: 'center',
		},
		disabled: { opacity: 0.45 },
		locked: {
			alignItems: 'center',
			backgroundColor: colors.input,
			borderColor: colors.inputBorder,
			borderRadius: s(8),
			borderWidth: Math.max(1, s(1)),
			columnGap: s(LAYOUT.lockedGap),
			flexDirection: 'row',
			height: s(LAYOUT.inputHeight),
			justifyContent: 'center',
			paddingHorizontal: s(LAYOUT.side),
		},
		actions: {
			columnGap: s(LAYOUT.actionGap),
			flexDirection: 'row',
			marginTop: s(LAYOUT.actionsTop),
		},
		cancel: {
			alignItems: 'center',
			backgroundColor: colors.input,
			borderColor: colors.cancelBorder,
			borderRadius: s(10),
			borderWidth: Math.max(1, s(1)),
			flex: 1,
			height: s(LAYOUT.actionHeight),
			justifyContent: 'center',
		},
		cancelLabel: {
			color: colors.cancelLabel,
			fontFamily: FONT,
			fontSize: s(18),
			fontWeight: '900',
			letterSpacing: s(0.36),
			lineHeight: s(23),
		},
		createButton: { flex: 1, height: s(LAYOUT.actionHeight) },
		createLabel: {
			color: colors.label,
			fontFamily: FONT,
			fontSize: s(18),
			fontWeight: '900',
			letterSpacing: s(0.36),
			lineHeight: s(23),
		},
	});
};

export default VipTablesModal;
